import {
  Box,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import ErrorMessageTypo from "./ErrorMessageTypo";
import _ from "lodash";
import Label from "./Label";
import ClearIcon from "@mui/icons-material/Clear";

export const BasicFormikSelect = ({
  field,
  fieldId,
  formik,
  options,
  placeholder,
  helperText,
  multiple = false,
  labelAlignement = null,
  disabled = false,
}) => {
  const label = field.spec.label.split("_")[0];
  const key = fieldId;
  const layoutSelect = formik?.special?.layout?.[key]?.selectWidth || "100%";
  const selectMinWidth = formik?.special?.layout?.[key]?.selectMinWidth || null;
  const canBeCleaned = formik?.special?.canBeCleaned?.[key] || false;

  /**
   * Retrieves the label(s) of the selected value(s) from the options array.
   *
   * @param {(string|string[])} selected - The selected value(s). Can be a single value or an array of values.
   * @param {Array<{value: string, label: string}>} options - The array of option objects, each containing a value and a label.
   * @returns {string} - The label(s) corresponding to the selected value(s), joined by a comma if multiple.
   */
  const getSelectedLabel = (selected, options) => {
    if (Array.isArray(selected)) {
      return selected
        .map((selectedValue) => {
          const value = selectedValue.value || selectedValue;

          return options.find((option) => option.value === value)?.label;
        })
        .join(", ");
    } else if (selected) {
      return options.find((option) => option.value === selected)?.label;
    }
  };

  return (
    <>
      {labelAlignement && <Label label={label} />}
      <FormGroup
        key={key}
        sx={{
          marginTop: 1,
          width: layoutSelect,
          minWidth: selectMinWidth,
        }}
      >
        {!labelAlignement && <InputLabel htmlFor={key}>{label}</InputLabel>}
        {helperText && (
          <>
            <Typography
              variant=""
              sx={{
                color: "text.secondary",
                fontSize: "0.65rem",
                fontWeight: "bold",
                fontStyle: "italic",
              }}
            >
              {helperText}
            </Typography>
          </>
        )}
        <Select
          multiple={multiple}
          displayEmpty
          name={key}
          id={key}
          size="small"
          disabled={disabled}
          placeholder={
            placeholder
              ? placeholder
              : "Select one" + multiple
              ? " or more"
              : ""
          }
          renderValue={(selected) => {
            if (_.isNumber(selected) && selected === 0) {
              return selected;
            }

            if (
              _.isUndefined(selected) ||
              (selected && selected.length === 0)
            ) {
              return <em>{placeholder}</em>;
            }

            return getSelectedLabel(selected, options);
          }}
          value={formik.values[key]}
          onChange={(e) => {
            formik.setFieldValue(key, e.target.value);
          }}
          error={formik.errors[key] ? true : false}
        >
          <MenuItem disabled value="">
            <em>{placeholder}</em>
          </MenuItem>
          {canBeCleaned && (
            <MenuItem value="">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ClearIcon />
                Clear selection
              </Box>
            </MenuItem>
          )}
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {formik.errors[key] ? (
          <ErrorMessageTypo error={formik.errors[key]} />
        ) : null}
      </FormGroup>
    </>
  );
};
